import { APPLICATION_STATUS } from '@/constants/applications'
import { EApplicationStatus, TApplication } from '@/interfaces'
import { ApplicationsState, AuthState, FormState } from '@/state'
import { applicationsStateActions } from '@/state/actions'
import { useComputed } from '@preact/signals-react'
import { Button, notification, Popconfirm, Tooltip } from 'antd'
import { useMemo } from 'react'

export default function ChangeStatusOption({
  statusOption,
  applicationInfo,
  loading,
}: {
  statusOption: EApplicationStatus
  applicationInfo: TApplication
  loading?: boolean
}) {
  const { statusInfo, hasPermission, isReview } = useMemo(() => {
    const statusInfo = APPLICATION_STATUS.find(
      (item) => item.value === statusOption
    )

    return {
      hasPermission: Boolean(
        statusInfo && AuthState.statusChangeAllowed.value.includes(statusOption)
      ),
      isReview: statusOption === EApplicationStatus.IN_REVIEW,
      statusInfo,
    }
  }, [statusOption, AuthState.statusChangeAllowed.value])

  const changingStatus = useComputed(() => {
    const own =
      ApplicationsState.loading.value.changeStatus.id === applicationInfo.id &&
      ApplicationsState.loading.value.changeStatus.status === statusOption
    const other = Boolean(
      ApplicationsState.loading.value.changeStatus.id && !own
    )
    return {
      own,
      other,
    }
  })

  if (
    !hasPermission ||
    (FormState.showForm.value && changingStatus.value.other) ||
    loading
  ) {
    return null
  }

  const handleChangeStatus = async () => {
    try {
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        changeStatus: {
          id: applicationInfo.id as string,
          status: statusOption,
        },
      }

      if (isReview) {
        await applicationsStateActions.initReview(applicationInfo.id as string)
      } else {
        await applicationsStateActions.changeStatus(
          statusOption,
          applicationInfo.id as string
        )
      }

      notification.success({
        message: 'Estado actualizado',
        description:
          'El estado de la solicitud se ha actualizado correctamente',
        placement: 'top',
      })
    } catch (_) {
      notification.error({
        message: 'Error actualizando el estado',
        description: 'Ocurrió un error actualizando el estado de la solicitud',
        placement: 'top',
      })
    } finally {
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        changeStatus: {},
      }
    }
  }

  return (
    <Popconfirm
      title={
        isReview
          ? '¿Estás seguro de iniciar la revisión de esta solicitud?'
          : `¿Estás seguro de cambiar el estado a ${statusInfo?.label}?`
      }
      onConfirm={handleChangeStatus}
      cancelButtonProps={{ disabled: changingStatus.value.own }}
      okButtonProps={{ loading: changingStatus.value.own }}
      placement='left'
    >
      <Tooltip
        title={
          isReview
            ? statusInfo?.label
            : `Marcar estado como: ${statusInfo?.label}`
        }
        placement='bottom'
      >
        <Button
          className={`action-btn ${statusInfo?.className || ''}`}
          shape='circle'
          icon={statusInfo?.icon}
          loading={changingStatus.value.own}
          disabled={changingStatus.value.other || loading}
        />
      </Tooltip>
    </Popconfirm>
  )
}
